import React, { useRef } from 'react';

export type PitchPlayer = {
  id: string;
  name: string;
  photoUrl?: string;
};

export type FormationPosition = {
  id: string;
  label: string;
  role: 'GK' | 'DEF' | 'MID' | 'FWD';
  xPercent: number; // 0-100 across width (0 = left touchline)
  yPercent: number; // 0-100 top to bottom (0 = attacking goal, 100 = own goal)
};

export interface Formation {
  name: string;
  positions: FormationPosition[];
}

/** Per-player match annotations (goals, assists, cards, substitutions). */
export interface PlayerAnnotation {
  goals?: number;
  assists?: number;
  yellowCard?: boolean;
  redCard?: boolean;
  subbedOff?: number;  // minute subbed off
  subbedOn?: number;   // minute subbed on
}

interface FormationPitchProps {
  formation: Formation;
  selected: Record<string, PitchPlayer | undefined>; // key = position.id
  annotations?: Record<string, PlayerAnnotation>; // key = position.id
  ratings?: Record<string, number>; // key = position.id, value = rating 1-100
  onClickPosition?: (position: FormationPosition) => void;
  onHoverPosition?: (position: FormationPosition, player: PitchPlayer | undefined, anchor: { clientX: number; clientY: number }) => void;
  onLeavePosition?: () => void;
  onClearPosition?: (posId: string) => void;
  descriptions?: Record<string, string>;
}

const badgeColors: Record<FormationPosition['role'], string> = {
  GK: 'bg-black text-white',
  DEF: 'bg-red-600 text-white',
  MID: 'bg-red-600 text-white',
  FWD: 'bg-red-600 text-white',
};

const ratingColor = (r: number) => {
  if (r >= 70) return 'bg-green-500';
  if (r >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
};

const FormationPitch: React.FC<FormationPitchProps> = ({ formation, selected, annotations, ratings, onClickPosition, onHoverPosition, onLeavePosition, onClearPosition, descriptions }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  return (
    <div className="w-full">
      {/* Pitch container with 3:4 aspect ratio */}
      <div
        ref={containerRef}
        className="relative w-full rounded-lg overflow-hidden shadow"
        style={{
          aspectRatio: '3 / 4',
          background: 'repeating-linear-gradient(0deg, #2e7d32, #2e7d32 18px, #2c6e31 18px, #2c6e31 36px)',
        }}
      >
        {/* Pitch lines */}
        <div className="absolute inset-2 border-2 border-white/80 rounded" />
        {/* Halfway line */}
        <div className="absolute left-2 right-2 top-1/2 border-t-2 border-white/80" />
        {/* Center circle */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 border-2 border-white/80 rounded-full" />
        {/* Penalty boxes */}
        <div className="absolute left-[15%] right-[15%] top-[8%] h-[10%] border-2 border-white/80" />
        <div className="absolute left-[25%] right-[25%] top-[4%] h-[6%] border-2 border-white/80" />
        <div className="absolute left-[15%] right-[15%] bottom-[8%] h-[10%] border-2 border-white/80" />
        <div className="absolute left-[25%] right-[25%] bottom-[4%] h-[6%] border-2 border-white/80" />

        {/* Players */}
        {formation.positions.map((pos) => {
          const player = selected[pos.id];
          const initials = player?.name
            ? player.name
                .split(' ')
                .map((s) => s.charAt(0).toUpperCase())
                .slice(0, 2)
                .join('')
            : pos.label;
          const jerseyClass = badgeColors[pos.role];
          const isGK = pos.role === 'GK';
          return (
            <button
              type="button"
              key={pos.id}
              onClick={(e) => {
                onClickPosition?.(pos);
                if (player) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  onHoverPosition?.(pos, player, {clientX: rect.left + rect.width / 2, clientY: rect.top + rect.height / 2});
                }
              }}
              onMouseEnter={(e) => {
                const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                const anchor = { clientX: rect.left + rect.width / 2, clientY: rect.top + rect.height / 2 };
                onHoverPosition?.(pos, player, anchor);
              }}
              onMouseLeave={() => onLeavePosition?.()}
              onFocus={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                onHoverPosition?.(pos, player, {clientX: rect.left + rect.width / 2, clientY: rect.top + rect.height / 2});
              }}
              onBlur={() => onLeavePosition?.()}
              title={`${pos.label}${player ? `: ${player.name}` : ''}`}
              className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-green-800"
              style={{ left: `${pos.xPercent}%`, top: `${pos.yPercent}%` }}
            >
              <div
                className={`relative flex items-center justify-center ${jerseyClass} rounded-full shadow-lg`}
                style={{ width: 44, height: 44, border: '2px solid rgba(255,255,255,0.85)' }}
              >
              {player && descriptions?.[pos.id] && <span className="sr-only">{descriptions[pos.id]}</span>}
                {player?.photoUrl && (
                  <div className="absolute inset-0 rounded-full overflow-hidden pointer-events-none z-0">
                    <img
                      src={player.photoUrl}
                      alt={player.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                {!player?.photoUrl && (
                  <span className="relative z-10 text-xs font-bold">
                    {isGK ? 'GK' : initials}
                  </span>
                )}
                {player && (
                  <div
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white text-red-600 border border-red-600 flex items-center justify-center text-[10px] cursor-pointer z-20"
                    onClick={(e) => { e.stopPropagation(); onClearPosition?.(pos.id); }}
                    title="Clear position"
                    aria-label="Clear position"
                    role="button"
                  >
                    ×
                  </div>
                )}
              </div>
              <div className="mt-1 px-2 py-0.5 rounded bg-white/80 text-gray-800 text-[10px] font-medium whitespace-nowrap flex items-center gap-1">
                <span>{player ? player.name : pos.label}</span>
                {ratings?.[pos.id] != null && (
                  <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-white text-[8px] font-bold ${ratingColor(ratings[pos.id])}`}>
                    {ratings[pos.id]}
                  </span>
                )}
              </div>
              {/* Match annotations (goals, assists, cards, subs) */}
              {annotations?.[pos.id] && (() => {
                const a = annotations[pos.id];
                const badges: React.ReactNode[] = [];
                if (a.goals) {
                  for (let g = 0; g < a.goals; g++) {
                    badges.push(<span key={`g${g}`} title="Goal">&#9917;</span>);
                  }
                }
                if (a.assists) {
                  for (let g = 0; g < a.assists; g++) {
                    badges.push(<span key={`a${g}`} title="Assist" className="text-blue-200">&#127380;</span>);
                  }
                }
                if (a.yellowCard) badges.push(<span key="yc" title="Yellow card" className="inline-block w-2.5 h-3.5 bg-yellow-400 rounded-[1px]" />);
                if (a.redCard) badges.push(<span key="rc" title="Red card" className="inline-block w-2.5 h-3.5 bg-red-600 rounded-[1px]" />);
                if (a.subbedOff !== undefined) badges.push(<span key="so" title={`Subbed off ${a.subbedOff}'`} className="text-red-300">&#8595;{a.subbedOff}'</span>);
                if (a.subbedOn !== undefined) badges.push(<span key="si" title={`Subbed on ${a.subbedOn}'`} className="text-green-300">&#8593;{a.subbedOn}'</span>);
                if (badges.length === 0) return null;
                return (
                  <div className="flex items-center gap-0.5 mt-0.5 text-[9px]">
                    {badges}
                  </div>
                );
              })()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FormationPitch;
